import React from "react";
import { Box, Typography } from "@mui/material";
import { CircleCheck, ChevronDown, Menu as MenuIcon } from "lucide-react";
import { ColorSchemeColors, buildCssVars, isDarkColor } from "../providers/color-scheme-provider";

// A static, self-contained mockup of the Transactions page (sidebar + topbar +
// a few representative rows) rendered with the *draft* colors the user is
// currently editing - not the live saved scheme. Every color in it comes from
// the same var(--color-x) tokens the real app uses, scoped locally via inline
// custom properties on the wrapper, so it stays a perfect stand-in for "what
// would this actually look like" without depending on real data/routing.
interface ColorSchemePreviewProps {
    colors: ColorSchemeColors;
}

const navItems = ['Transactions', 'Provisions', 'Invoices', 'Staff Payroll', 'Summaries', 'Settings'];

const mockRows: Array<{
    kind: 'horizon' | 'real' | 'provision' | 'simulation' | 'currentDay' | 'oneTime' | 'recurring' | 'invoice';
    date: string;
    amount: string;
    negative?: boolean;
    description: string;
    category: string;
}> = [
    { kind: 'real', date: '29 Jul 2026', amount: '₱ 100.00', description: 'Invoice #1177 - D\'Amore', category: 'Other Revenue' },
    { kind: 'currentDay', date: '27 Jul 2026', amount: '₱ 10.00', description: 'Office supplies', category: 'Expenses' },
    { kind: 'oneTime', date: '25 Jul 2026', amount: '₱ 400.00', description: 'One-Time Provision', category: 'Equipment' },
    { kind: 'recurring', date: '20 Jul 2026', amount: '₱ 250.00', description: 'Recurring Provision', category: 'Rent' },
    { kind: 'invoice', date: '19 Jul 2026', amount: '₱ 950.00', description: 'Invoice Provision', category: 'Revenue' },
    { kind: 'real', date: '18 Jul 2026', amount: '₱ 31.88', negative: true, description: 'Stripe fee', category: 'Bank Fees' },
    { kind: 'simulation', date: '15 Jul 2026', amount: '₱ 500.00', description: 'What-if: extra client', category: 'Other Revenue' },
];

const ColorSchemePreview: React.FC<ColorSchemePreviewProps> = ({ colors }) => {
    const cssVars = buildCssVars(colors);
    const dark = isDarkColor(colors.pageBackgroundColor);

    const rowBg = (row: typeof mockRows[number], idx: number) => {
        if (row.kind === 'horizon') return 'var(--color-end-of-horizon-bg)';
        if (row.kind === 'simulation') return 'var(--color-simulation-row-bg)';
        if (row.kind === 'currentDay') return 'var(--color-current-day-bg)';
        if (row.kind === 'oneTime') return 'var(--color-onetime-provision-bg)';
        if (row.kind === 'recurring') return 'var(--color-recurring-provision-bg)';
        if (row.kind === 'invoice') return 'var(--color-invoice-provision-bg)';
        const alt = idx % 2 === 0;
        if (row.kind === 'provision') return alt ? 'var(--color-provision-row-bg)' : 'rgba(var(--color-provision-row-bg-rgb), 0.5)';
        return alt ? 'var(--color-transaction-row-bg)' : 'rgba(var(--color-transaction-row-bg-rgb), 0.5)';
    };

    const rowText = (row: typeof mockRows[number]) => {
        if (row.kind === 'oneTime') return 'var(--color-onetime-provision-text)';
        if (row.kind === 'recurring') return 'var(--color-recurring-provision-text)';
        if (row.kind === 'invoice') return 'var(--color-invoice-provision-text)';
        return row.kind === 'provision' ? 'var(--color-provision-text)' : 'var(--color-transaction-text)';
    };

    return (
        <Box>
            <Typography fontSize={14} fontWeight={600} mb={1}>Preview</Typography>
            <Typography fontSize={12} sx={{ color: 'var(--color-text-muted)', mb: 1.5 }}>
                A mock Transactions page showing your unsaved changes live.
            </Typography>
            <Box
                sx={{
                    ...(cssVars as React.CSSProperties),
                    width: '100%',
                    maxWidth: 480,
                    border: '1px solid #d0d0d0',
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                    background: 'var(--color-container-background)',
                    display: 'flex',
                    height: 520,
                }}
            >
                {/* Sidebar */}
                <Box sx={{ width: 92, background: 'var(--color-page-background)', display: 'flex', flexDirection: 'column', py: 1.5, boxSizing: 'border-box', flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--color-primary)', px: 1.5, mb: 1.5 }}>
                        FineAnts
                    </Typography>
                    {navItems.map((item) => {
                        const active = item === 'Transactions';
                        return (
                            <Box
                                key={item}
                                sx={{
                                    mx: 0.75,
                                    mb: 0.5,
                                    px: 1,
                                    py: 0.6,
                                    borderRadius: 1,
                                    background: active ? 'var(--color-primary)' : 'transparent',
                                    color: active ? '#fff' : 'var(--color-default-text)',
                                }}
                            >
                                <Typography sx={{ fontSize: 9.5, fontWeight: active ? 700 : 500, lineHeight: 1.2 }}>
                                    {item}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

                {/* Main column */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    {/* Topbar */}
                    <Box sx={{ background: 'var(--color-page-background)', borderBottom: '1px solid rgba(128,128,128,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, flexShrink: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <MenuIcon size={14} color="var(--color-default-text)" />
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--color-default-text)' }}>Transactions</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography sx={{ fontSize: 9.5, color: 'var(--color-default-text)', textAlign: 'right', lineHeight: 1.1 }}>
                                Development
                            </Typography>
                            <ChevronDown size={12} color="var(--color-text-muted)" />
                        </Box>
                    </Box>

                    {/* Filter strip + cashflow banner */}
                    <Box sx={{ background: 'var(--color-page-background)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, gap: 1, flexShrink: 0 }}>
                        <Box sx={{ border: '1px solid var(--color-primary)', borderRadius: 1, px: 1, py: 0.4 }}>
                            <Typography sx={{ fontSize: 9, color: 'var(--color-default-text)' }}>Real Transactions</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, background: 'rgba(var(--color-cashflow-positive-bg-rgb), 0.2)', borderRadius: 1, px: 1, py: 0.5, flex: 1, minWidth: 0 }}>
                            <CircleCheck size={12} color="var(--color-cashflow-positive-text)" />
                            <Typography sx={{ fontSize: 9, fontWeight: 600, color: 'var(--color-cashflow-positive-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                Your company never runs out of money
                            </Typography>
                        </Box>
                    </Box>

                    {/* Table */}
                    <Box sx={{ flex: 1, overflow: 'hidden', px: 1.5, pb: 1.5 }}>
                        <Box sx={{ borderRadius: 1, overflow: 'hidden', border: '1px solid rgba(128,128,128,0.2)' }}>
                            <Box sx={{ display: 'flex', background: 'var(--color-container-background)', px: 1, py: 0.6 }}>
                                {['Date', 'Amount', 'Description', 'Category'].map((h) => (
                                    <Typography key={h} sx={{ fontSize: 8.5, fontWeight: 700, color: 'var(--color-default-text)', flex: h === 'Description' ? 1.6 : 1 }}>
                                        {h}
                                    </Typography>
                                ))}
                            </Box>
                            <Box sx={{ display: 'flex', background: 'var(--color-end-of-horizon-bg)', px: 1, py: 0.4, justifyContent: 'center' }}>
                                <Typography sx={{ fontSize: 8, fontWeight: 600, color: 'var(--color-end-of-horizon-text)' }}>
                                    End of Horizon
                                </Typography>
                            </Box>
                            {mockRows.map((row, idx) => (
                                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', background: rowBg(row, idx), px: 1, py: 0.55 }}>
                                    <Typography sx={{ fontSize: 8.5, color: rowText(row), flex: 1 }}>{row.date}</Typography>
                                    <Typography sx={{ fontSize: 8.5, color: row.negative ? 'var(--color-negative-amount)' : rowText(row), flex: 1 }}>
                                        {row.negative ? `- ${row.amount}` : row.amount}
                                    </Typography>
                                    <Typography sx={{ fontSize: 8.5, color: rowText(row), flex: 1.6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {row.description}
                                    </Typography>
                                    <Typography sx={{ fontSize: 8.5, color: rowText(row), flex: 1 }}>{row.category}</Typography>
                                </Box>
                            ))}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, background: 'var(--color-current-day-bg)', borderRadius: 1, px: 1, py: 0.4 }}>
                                <Typography sx={{ fontSize: 7.5, color: 'var(--color-default-text)' }}>● Current day</Typography>
                            </Box>
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, background: 'var(--color-simulation-row-bg)', borderRadius: 1, px: 1, py: 0.4, border: dark ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.1)' }}>
                                <Typography sx={{ fontSize: 7.5, color: 'var(--color-default-text)' }}>● Simulation</Typography>
                            </Box>
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, background: 'var(--color-run-out-of-money-bg)', borderRadius: 1, px: 1, py: 0.4 }}>
                                <Typography sx={{ fontSize: 7.5, color: 'var(--color-default-text)' }}>● Runs out</Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default ColorSchemePreview;
